import { useId, useState, type ComponentProps } from 'react';
import { Check, Eye, EyeOff, Circle } from 'lucide-react';
import { Input } from '@/components/ui';
import { passwordChecks, passwordScore } from '@/lib/password';
import { cn } from '@/lib/cn';

type Props = Omit<ComponentProps<typeof Input>, 'type' | 'trailing'> & { showStrength?: boolean };

export function PasswordField({ showStrength, value, ...props }: Props) {
  const [visible, setVisible] = useState(false);
  const hintsId = useId();
  const pw = typeof value === 'string' ? value : '';
  const checks = passwordChecks(pw);
  const score = passwordScore(pw);
  const strengthLabel = ['', 'Weak', 'Fair', 'Good', 'Strong'][score];

  return (
    <div>
      <Input
        {...props}
        value={value}
        type={visible ? 'text' : 'password'}
        aria-describedby={showStrength ? hintsId : undefined}
        trailing={
          <button
            type="button"
            onClick={() => setVisible((v) => !v)}
            aria-label={visible ? 'Hide password' : 'Show password'}
            aria-pressed={visible}
            className="grid size-9 place-items-center rounded-lg text-ink-muted transition hover:bg-hover hover:text-ink"
          >
            {visible ? <EyeOff className="size-[18px]" /> : <Eye className="size-[18px]" />}
          </button>
        }
      />
      {showStrength && (
        <div id={hintsId} className="mt-3">
          <div className="flex items-center gap-2" aria-hidden="true">
            <div className="flex flex-1 gap-1">
              {[1, 2, 3, 4].map((i) => (
                <span
                  key={i}
                  className={cn(
                    'h-1.5 flex-1 rounded-full bg-surface-muted transition-colors',
                    score >= i && (score <= 1 ? '!bg-danger-500' : score === 2 ? '!bg-gold-500' : '!bg-leaf-500'),
                  )}
                />
              ))}
            </div>
            <span className="w-12 text-right text-xs font-medium text-ink-muted">{strengthLabel}</span>
          </div>
          <ul className="mt-2.5 grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2" aria-label="Password requirements">
            {checks.map((c) => (
              <li key={c.id} className={cn('flex items-center gap-1.5 text-xs', c.met ? 'text-leaf-600 dark:text-green-300' : 'text-ink-muted')}>
                {c.met ? <Check className="size-3.5" aria-hidden="true" /> : <Circle className="size-3" aria-hidden="true" />}
                {c.label}
                <span className="sr-only">{c.met ? ' (met)' : ' (not met)'}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
